import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Route, X } from "lucide-react";
import { useTranslate } from "../context/TranslateContext";
import { ALL_LOCATIONS, LOC_COORDS, INDIA_MAP_IMG } from "../mock/data";

export const IndiaMap = () => {
  const { t } = useTranslate();
  const pts = ALL_LOCATIONS.map((l, i) => ({ ...l, i, c: LOC_COORDS[l.city] })).filter((p) => p.c);
  const [active, setActive] = useState(null);
  const [route, setRoute] = useState([]); // array of indices into pts

  const inRoute = (i) => route.includes(i);
  const toggleRoute = (i) => setRoute((r) => r.includes(i) ? r.filter((x) => x !== i) : [...r, i]);

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
      {/* Map */}
      <div className="lg:col-span-7">
        <div className="relative overflow-hidden rounded-3xl border border-edge bg-[#100c12]" data-testid="india-map">
          <img src={INDIA_MAP_IMG} alt="Map of India" className="w-full opacity-70 mix-blend-screen" style={{ filter: "brightness(1.6) sepia(0.4) hue-rotate(-8deg) saturate(1.4)" }} />
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {route.length > 1 && (
              <polyline fill="none" stroke="#D4AF37" strokeWidth="0.5" strokeDasharray="1.5 1.5" strokeLinejoin="round"
                points={route.map((ri) => `${pts[ri].c.x},${pts[ri].c.y}`).join(" ")} />
            )}
          </svg>
          {pts.map((p) => (
            <button key={p.i} data-testid={`map-pin-${p.i}`}
              onMouseEnter={() => setActive(p.i)} onFocus={() => setActive(p.i)}
              onClick={() => toggleRoute(p.i)}
              className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.c.x}%`, top: `${p.c.y}%` }}>
              <span className="relative flex items-center justify-center">
                {inRoute(p.i) && <span className="absolute -inset-2 animate-ping rounded-full bg-gold/40" />}
                <MapPin className={`h-6 w-6 drop-shadow-lg transition-transform duration-200 hover:scale-125 ${inRoute(p.i) ? "fill-gold text-gold" : "fill-crimson text-cream"}`} />
              </span>
              {active === p.i && (
                <span className="absolute left-1/2 top-7 z-10 w-max -translate-x-1/2 rounded-lg border border-edge bg-surface px-3 py-1.5 text-center">
                  <span className="block font-body text-xs text-gold">{p.name}</span>
                  <span className="block font-body text-[0.65rem] text-ash">{p.movie.title}</span>
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="mt-3 font-body text-xs text-ash">{t("Tap a pin to add it to your route. Hover to preview the film.")}</p>
      </div>

      {/* Route planner */}
      <div className="lg:col-span-5">
        <div className="rounded-3xl border border-edge bg-surface/40 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-display text-2xl text-cream"><Route className="h-5 w-5 text-gold" /> {t("Your Film Route")}</div>
            {route.length > 0 && <button onClick={() => setRoute([])} data-testid="clear-route" className="inline-flex items-center gap-1 font-body text-xs text-ash hover:text-crimson"><X className="h-3.5 w-3.5" />{t("Clear")}</button>}
          </div>

          {route.length === 0 ? (
            <p className="mt-4 font-body text-sm text-ash" data-testid="route-empty">{t("Select spots on the map to build a cinematic road trip across India.")}</p>
          ) : (
            <ol className="mt-4 space-y-3" data-testid="route-list">
              {route.map((ri, order) => { const p = pts[ri]; return (
                <li key={ri} className="flex items-center gap-3 rounded-xl border border-edge bg-surface p-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold font-body text-sm font-medium text-ink">{order + 1}</span>
                  <span className="min-w-0 flex-1">
                    <Link to={`/movie/${p.movie.id}`} className="block truncate font-body text-cream hover:text-gold">{p.name}</Link>
                    <span className="block truncate font-body text-xs text-ash">{p.city}, {p.state} · {p.movie.title}</span>
                  </span>
                  <button onClick={() => toggleRoute(ri)} className="text-ash hover:text-crimson"><X className="h-4 w-4" /></button>
                </li>
              ); })}
            </ol>
          )}

          {route.length > 1 && (
            <a href="https://keralatravels.com" target="_blank" rel="noopener noreferrer" data-testid="book-route"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 font-body font-medium text-ink transition-transform hover:scale-[1.02]">
              {t("Plan this trip with our travel partners")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
